/**
 * Sales Filters — reusable filter bar for view (my book)/entity/division/salesperson.
 *
 * Renders into a container, persists to sessionStorage, and calls
 * onFilterChange(filters) when any selection changes.
 */
import { salesApi } from '../services/sales-api.js';
import { store } from '@isg/ui-kit';

const SI_FILTER_KEY = 'si_filters';
let _filtersCache = {};

const ENTITY_LABEL = { uk: 'UK', us: 'USA', de: 'Germany' };

/** The list date ranges — the server's `helpers.PERIOD_DAYS`, anchored on the newest ERP day.
 *  One set, one remembered choice, on every page that opts in (`periods: true`). */
export const PERIODS = [
  ['7d', 'Last 7 days'], ['30d', 'Last 30 days'], ['90d', 'Last 90 days'],
  ['12m', 'Last 12 months'], ['all', 'Any date'],
];
const SI_PERIOD_KEY = 'si_period';

/** The remembered date range (or `fallback`) — for a list that carries its own period select
 *  instead of the page bar's, e.g. one tab of a page whose other tabs have no dates. */
export function savedPeriod(fallback = 'all') {
  let v = null;
  try { v = sessionStorage.getItem(SI_PERIOD_KEY); } catch { /* ignore */ }
  return PERIODS.some(([k]) => k === v) ? v : fallback;
}

export function savePeriod(v) {
  try { sessionStorage.setItem(SI_PERIOD_KEY, v); } catch { /* ignore */ }
}

/** The date-range `<select>` markup, the same choices everywhere. */
export function periodSelect(id, value) {
  return `<select class="form-select" id="${id}" style="width:auto;font-size:var(--text-sm)" aria-label="Date range">
    ${PERIODS.map(([k, l]) => `<option value="${k}"${k === value ? ' selected' : ''}>${l}</option>`).join('')}
  </select>`;
}

/**
 * Render filter dropdowns into a container.
 *
 * The entities offered come from the server (`/api/sales/filters` → `entities`):
 * the companies ISG Admin grants this person, as entities (app/platform/scope.py).
 * A saved entity that is no longer in reach is dropped for the server's default,
 * rather than asking for it and getting a 403.
 *
 * "My book" (a View select) appears only when the person holds a confirmed rep code
 * in the entity (`/filters` → `my_book.available`, Team › Rep codes). It is the
 * default view for them (plan: own book by default, never a boundary); everyone else
 * sees the whole entity with no extra control.
 *
 * @param {HTMLElement} container
 * @param {Object} opts
 * @param {Function} opts.onFilterChange — called with { entity, division, salesperson, book }
 * @param {boolean} opts.showSalesperson — show the salesperson view filter
 * @param {boolean|string} opts.periods — show the date-range select (`period` param); a
 *   string is the default when the person has not chosen one ('all' otherwise)
 * @returns {{ getParams: Function, ready: Promise }} — await `ready` before the first load
 */
/** A floor manager's (sales_manager) book IS the whole floor — say so, rather than
 *  "My book" for a view that shows every rep's accounts. */
function floorManager() {
  return (((store.get('user') || {}).app_access || {}).sales || {}).role === 'sales_manager';
}

export function renderFilters(container, { onFilterChange, showSalesperson = true, periods = false }) {
  const saved = _restoreFilters();
  const period0 = savedPeriod(typeof periods === 'string' ? periods : 'all');

  container.innerHTML = `
    <div style="display:flex;gap:var(--space-2);flex-wrap:wrap;align-items:center">
      <select class="form-select" id="si-book" style="width:auto;font-size:var(--text-sm)" aria-label="View" hidden>
        <option value="mine">${floorManager() ? 'Whole floor' : 'My book'}</option>
        <option value="all">Everyone</option>
      </select>
      <select class="form-select" id="si-entity" style="width:auto;font-size:var(--text-sm)" aria-label="Entity">
        ${saved?.entity ? `<option value="${esc(saved.entity)}" selected>${esc(ENTITY_LABEL[saved.entity] || saved.entity)}</option>` : ''}
      </select>
      <select class="form-select" id="si-division" style="width:auto;font-size:var(--text-sm)" aria-label="Division">
        <option value="all">All Divisions</option>
      </select>
      ${showSalesperson ? `
        <select class="form-select" id="si-salesperson" style="width:auto;font-size:var(--text-sm)" aria-label="Salesperson">
          <option value="all">All Salespeople</option>
        </select>
      ` : ''}
      ${periods ? `
        ${periodSelect('si-period', period0)}
      ` : ''}
    </div>
  `;

  const entityEl = container.querySelector('#si-entity');
  const divisionEl = container.querySelector('#si-division');
  const salespersonEl = container.querySelector('#si-salesperson');
  const bookEl = container.querySelector('#si-book');
  const periodEl = container.querySelector('#si-period');
  // What the person last chose, kept even while the control is hidden (an entity
  // where they hold no code) so it comes back when they switch back.
  let bookChoice = saved?.book || null;

  function getFilters() {
    const entity = entityEl?.value || '';
    const division = divisionEl?.value || 'all';
    const salesperson = salespersonEl?.value || 'all';
    const book = bookEl && !bookEl.hidden ? bookEl.value : (bookChoice || 'all');
    return { entity, division, salesperson, book };
  }

  function saveFilters() {
    try {
      sessionStorage.setItem(SI_FILTER_KEY, JSON.stringify(getFilters()));
    } catch { /* ignore */ }
  }

  function buildParams() {
    const f = getFilters();
    const p = {};
    if (f.entity) p.entity = f.entity;
    if (f.division && f.division !== 'all') p.division = f.division;
    if (f.salesperson && f.salesperson !== 'all') p.salesperson = f.salesperson;
    if (bookEl && !bookEl.hidden && bookEl.value === 'mine') p.book = 'mine';
    if (periodEl) p.period = periodEl.value;
    return p;
  }

  async function fetchOptions(entity) {
    try {
      return await salesApi.getFilters(entity ? { entity } : {});
    } catch {
      // Not in reach (403) or unavailable: let the server choose the entity.
      if (entity) {
        try { return await salesApi.getFilters({}); } catch { /* fall through */ }
      }
      return { entities: [], divisions: [], salespeople: [] };
    }
  }

  async function loadFilterOptions() {
    const data = await fetchOptions(entityEl?.value || '');
    const entity = data.entity || '';
    _filtersCache[entity] = data;

    const entities = data.entities || (entity ? [entity] : []);
    entityEl.innerHTML = entities.map(e =>
      `<option value="${esc(e)}"${e === entity ? ' selected' : ''}>${esc(ENTITY_LABEL[e] || e)}</option>`
    ).join('');
    entityEl.disabled = entities.length <= 1;

    if (divisionEl) {
      const cur = divisionEl.value;
      divisionEl.innerHTML = '<option value="all">All Divisions</option>' +
        (data.divisions || []).map(d =>
          `<option value="${esc(d.code)}">${esc(d.code)} — ${esc(d.name)}</option>`
        ).join('');
      if (cur && [...divisionEl.options].some(o => o.value === cur)) divisionEl.value = cur;
    }

    if (salespersonEl) {
      const cur = salespersonEl.value;
      salespersonEl.innerHTML = '<option value="all">All Salespeople</option>' +
        (data.salespeople || []).map(s =>
          `<option value="${esc(s.code)}">${esc(s.name)}</option>`
        ).join('');
      if (cur && [...salespersonEl.options].some(o => o.value === cur)) salespersonEl.value = cur;
    }

    if (bookEl) {
      const available = !!(data.my_book && data.my_book.available);
      bookEl.hidden = !available;
      if (available) bookEl.value = bookChoice === 'all' ? 'all' : 'mine';
    }
    saveFilters();
  }

  function onChange() {
    saveFilters();
    if (onFilterChange) onFilterChange(buildParams());
  }

  entityEl.addEventListener('change', async () => {
    saveFilters();
    await loadFilterOptions();
    onChange();
  });

  divisionEl.addEventListener('change', onChange);
  if (bookEl) bookEl.addEventListener('change', () => { bookChoice = bookEl.value; onChange(); });
  if (salespersonEl) salespersonEl.addEventListener('change', onChange);
  if (periodEl) periodEl.addEventListener('change', () => {
    savePeriod(periodEl.value);
    onChange();
  });

  // Initial load — restore saved division/salesperson only if still offered.
  const ready = loadFilterOptions().then(() => {
    if (saved) {
      if (divisionEl && saved.division && [...divisionEl.options].some(o => o.value === saved.division)) {
        divisionEl.value = saved.division;
      }
      if (salespersonEl && saved.salesperson && [...salespersonEl.options].some(o => o.value === saved.salesperson)) {
        salespersonEl.value = saved.salesperson;
      }
      saveFilters();
    }
  });

  return { getParams: buildParams, ready };
}

function _restoreFilters() {
  try {
    const raw = sessionStorage.getItem(SI_FILTER_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

/**
 * Get the currently saved filter params from sessionStorage.
 * Usable from pages that don't render their own filter bar (e.g. customer-detail).
 */
export function getSavedFilterParams() {
  const saved = _restoreFilters();
  const p = {};
  if (saved && saved.entity) p.entity = saved.entity;
  if (saved && saved.division && saved.division !== 'all') p.division = saved.division;
  if (saved && saved.salesperson && saved.salesperson !== 'all') p.salesperson = saved.salesperson;
  return p;
}

/** Map entity code to default currency. */
/** The entity's currency — mirrors isg_common.fx.ENTITY_CURRENCY (the estate's one definition,
 *  which commercial.HOME_CURRENCY imports). Every value the API returns is in it (entity_value). */
export function entityCurrency(entity) {
  return { uk: 'GBP', us: 'USD', de: 'EUR' }[entity] || 'GBP';
}

function esc(str) {
  const d = document.createElement('div');
  d.textContent = str || '';
  return d.innerHTML;
}
